import { useEffect, useState, useRef } from 'react';
import { Download, Trash2, Plus, Eye, Loader2, X, Upload, Undo2, MoreHorizontal, MoreVertical, FileStack, Tag, ChevronDown, CheckCircle2, AlertCircle, RefreshCw, Send } from 'lucide-react';
import { createPortal } from 'react-dom';
import { dokumentLabel, baseCategory } from './documentSets';
import { PageEditor } from './PageEditor';
import { HinweisPopover } from './HinweisPopover';
import { PdfScrollViewer } from './PdfScrollViewer';
import { listFiles, uploadFile, uploadSignedVn, getBerichtVersand, deleteFile, restoreFile, fetchFileBlob, setFileSigned, countersignFile, undoCountersign, reorderFiles, renameFile, setFileCategory, regenerateDauerdokument, previewDauerSigning, prepareSigningDauer, type UploadedFileDto } from '../../lib/vorgangApi';
import SigningEmailReview from '../SigningEmailReview';
import { type SigningPreview } from '../SigningPreviewDialog';

/** Dauer-Dokument-Grundtypen ↔ API-Typ-Slug (für "Neu generieren"). */
const DAUER_TYP_SLUG: Record<string, 'vollmacht' | 'grc' | 'de_minimis'> = {
  vollmacht: 'vollmacht',
  grc: 'grc',
  'de-minimis': 'de_minimis',
};
const ALLE_DAUER_TYPEN = Object.values(DAUER_TYP_SLUG);

const clamp2: React.CSSProperties = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word', overflowWrap: 'anywhere' };

type Level = { customerId: number } | { vorgangId: number };

function isSigned(cat: string | null) { return !!cat && cat.toLowerCase().includes('unterschrieben'); }
/** Belege, die nicht unterschrieben werden, aber als „erledigt" gelten, sobald sie
 *  hochgeladen sind (Rechnung, Kontoauszug) → ebenfalls grün/✓. */
function isReceiptType(cat: string | null) {
  const c = (cat || '').toLowerCase();
  return c.includes('rechnung') || c.includes('kontoauszug');
}
/** Grün/✓: unterschrieben ODER ein vorhandener Beleg (Rechnung/Kontoauszug). */
function isComplete(cat: string | null) { return isSigned(cat) || isReceiptType(cat); }
/** Bericht, der nur vom Kunden unterschrieben ist (Berater-Unterschrift fehlt noch) → gelb.
 *  Dauer-Doks (Vollmacht/GRC/De-Minimis – Unterschrieben) gelten mit EINER Unterschrift als
 *  vollständig und bleiben grün. */
function isPartiallySigned(cat: string | null) {
  const c = (cat || '').toLowerCase();
  return c.includes('unterschrieben') && c.includes('bericht') && !c.includes('vollständig');
}
/** Roher Verwendungsnachweis (hochgeladen, wartet auf die unterschriebenen Seiten) → orange.
 *  Bekommt oben links einen Upload-Knopf; der Rücklauf wird gemergt und die Karte wird blau. */
function isVnRaw(cat: string | null) {
  return (cat || '').trim().toLowerCase() === 'verwendungsnachweis';
}
/** Fertig unterschriebener VN. Er ist abgeschlossen wie jedes andere fertige Dokument und
 *  damit GRÜN — ein eigenes Blau widersprach der Lesegewohnheit „grün = fertig". Damit die
 *  Schlussmarke des Vorgangs trotzdem auffindbar bleibt, trägt die Karte zusätzlich das
 *  Kürzel „VN". */
function isVnComplete(cat: string | null) {
  const c = (cat || '').toLowerCase();
  return c.includes('verwendungsnachweis') && c.includes('unterschrieben');
}
/** Noch nicht unterschriebener Bericht. Ist er zur Unterschrift verschickt, wird seine
 *  Karte orange — genau wie der rohe VN, der auf die unterschriebenen Seiten wartet. */
export function isBerichtRoh(cat: string | null) {
  return (cat || '').trim().toLowerCase() === 'bericht pdf';
}

/** Tooltip der orange Karte: nennt anhand des erkannten Signatur-Status genau, WESSEN
 *  Unterschrift noch fehlt. Fällt auf „Kunde unterschrieben" zurück (häufigster Fall). */
function partialSignedTooltip(sig?: 'kunde' | 'berater' | 'beide' | null): string {
  if (sig === 'berater') return 'Nur vom Berater unterschrieben – Kunden-Unterschrift fehlt noch';
  return 'Nur vom Kunden unterschrieben – Berater-Unterschrift fehlt noch';
}

/** Tooltip der roten "zur Prüfung"-Karte: nennt den konkreten Grund statt nur generisch
 *  "Zuordnung/Inhalt unklar" — damit Berater (und wir) sofort wissen, woran es liegt, ohne
 *  nachzufragen (siehe needsReviewReason in file.entity.ts). Unbekannte/alte Codes fallen auf
 *  den bisherigen generischen Text zurück. */
const NEEDS_REVIEW_REASON_LABEL: Record<string, string> = {
  'kein-original-vn': 'Kein anderes Verwendungsnachweis-Dokument am Vorgang zum Zusammenführen gefunden — „Als vollständig markieren“ im Menü, falls diese Datei bereits der vollständige unterschriebene VN ist.',
  'unsicher': 'Die Unterschriftsseiten konnten nicht sicher erkannt werden.',
  'seiten-mismatch': 'Die Seitenzahl passt nicht zu den erkannten Unterschriftsseiten.',
  'reihenfolge-unklar': 'Die Reihenfolge der zurückgeschickten Seiten ist unklar.',
  'merge-fehler': 'Das Zusammenführen mit dem Original ist fehlgeschlagen.',
  'fehler': 'Bei der automatischen Prüfung ist ein Fehler aufgetreten.',
  'keine-unterschrift': 'Es wurde keine Unterschrift erkannt, obwohl ein signierter Rücklauf angekündigt war.',
  'fremder-inhaber': 'Der Inhalt (Name/Firma) passt nicht sicher zu diesem Kunden.',
  'inhalt-abweichend': 'Der erkannte Inhalt widerspricht der gewählten Kategorie.',
  'unsichere-klassifizierung': 'Der Dokumenttyp konnte nicht sicher erkannt werden.',
  'unsicher-nach-erneuter-pruefung': 'Auch die erneute Prüfung konnte den Dokumenttyp nicht sicher erkennen.',
  'unsicherer-dokumenttyp': 'Der Dokumenttyp konnte nicht sicher erkannt werden.',
  'fragment-trotz-vollfassung': 'Enthält nur einen Teil, am Kunden liegt aber bereits eine vollständigere Fassung.',
  'foto-ohne-unterschrift': 'Auf dem Foto wurde keine Unterschrift erkannt.',
  'anonymer-upload-ungeprueft': 'Anonym über einen Signatur-Link hochgeladen, inhaltlich noch ungeprüft.',
  'mailtext-bestaetigt-typ-nicht': 'Der Mailtext bestätigt den Dokumenttyp nicht eindeutig.',
  'bafa-eingangsbestaetigung-kopie': 'BAFA-Eingangsbestätigung — vermutlich nur eine Kopie bereits vorhandener Unterlagen, meist unbedenklich.',
};
function needsReviewTooltip(reason?: string | null): string {
  const grund = reason ? NEEDS_REVIEW_REASON_LABEL[reason] : null;
  return grund
    ? `Zur Prüfung — ${grund}`
    : 'Zur Prüfung — Zuordnung/Inhalt unklar. „Kategorie ändern“ im Menü';
}

/** Kurzes Typ-Label für das Datei-Icon: Endung bevorzugt, sonst aus MIME-Typ. */
function fileTypeLabel(f: { originalName?: string; name?: string; mimeType?: string }): string {
  const ext = (f.originalName || f.name || '').match(/\.([a-z0-9]+)$/i);
  if (ext) return ext[1].toUpperCase();
  const mt = f.mimeType || '';
  if (mt.includes('pdf')) return 'PDF';
  if (mt.startsWith('image/')) return mt.split('/')[1].toUpperCase();
  return 'DATEI';
}

/** Wie die Kacheln eingeklappt werden:
 *  - 'immer'             → immer nur eine Statuszeile; Kacheln erst auf Klick (Dauer-Dokumente,
 *                          die einmal hochgeladen werden und danach nichts mehr brauchen).
 *  - 'wenn-vollstaendig' → sind alle Soll-Dokumente da, bleibt nur eine grüne Sammelzeile;
 *                          fehlt etwas, stehen die Kacheln sofort offen (Vorgang).
 *  Ohne die Angabe verhält sich das Grid wie bisher (Kacheln immer sichtbar). */
export type CollapseMode = 'immer' | 'wenn-vollstaendig';

/** Kurze Erklärung der Dauer-Dokumente — sie steht als Tooltip in der Statuszeile,
 *  solange noch nicht alle vorliegen. */
const DAUER_ERKLAERUNG = [
  'Dauer-Dokumente unterschreibt der Kunde einmal je Kalenderjahr; sie gelten dann für alle seine Vorgänge:',
  '',
  '• Vollmacht — erlaubt dir, gegenüber der BAFA für den Kunden zu handeln.',
  '• GRC — Erklärung des Kunden zu verbundenen Unternehmen und Größenklasse.',
  '• De-Minimis — Übersicht der Beihilfen, die der Kunde bereits erhalten hat.',
  '',
  'Rechts über „BAFA-Formulare generieren" erzeugen, zur Unterschrift senden — unterschrieben erscheinen sie hier.',
].join('\n');

/** 'kacheln' = Kachelraster auf dem Desktop (Vorgang), 'liste' = überall die
 *  kompakten Zeilen (Dauer-Dokumente: wenige, feste Typen — als Raster wirken sie
 *  wie eine Wand, als Liste liest man sie in einer Spalte ab). */
export type GridLayout = 'kacheln' | 'liste';

export function DocumentGrid({ title, level, soll, categories, signedOnly, onChanged, refreshKey, collapse, onStatus, layout = 'kacheln', uploadNurWennUnvollstaendig, customerEmail }: { title: string; level: Level; soll: string[]; categories: string[]; signedOnly?: boolean; onChanged?: () => void; refreshKey?: number; collapse?: CollapseMode; onStatus?: (s: { vorhanden: number; gesamt: number; fehlend: string[]; fehlendAktuell: string[] }) => void; layout?: GridLayout; uploadNurWennUnvollstaendig?: boolean; customerEmail?: string | null }) {
  const [files, setFiles] = useState<UploadedFileDto[]>([]);
  const [pendingDelete, setPendingDelete] = useState<UploadedFileDto | null>(null);
  // Sicherheitsabfrage vor "Gegenzeichnung rückgängig" — ändert eine echte signierte
  // Fassung ohne weitere Bestätigung war ein Fehlklick-Risiko (siehe Vorfall Francesco).
  const [pendingUndoCountersign, setPendingUndoCountersign] = useState<UploadedFileDto | null>(null);
  const [lastDeleted, setLastDeleted] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const levelKey = JSON.stringify(level);
  // Erst nach dem ersten Laden steht fest, was fehlt — vorher bleibt eine eingeklappte
  // Ansicht zu, sonst blitzt die Kachelwand kurz auf und klappt wieder weg.
  const [geladen, setGeladen] = useState(false);
  const load = () => { listFiles(level).then(setFiles).catch(() => setFiles([])).finally(() => setGeladen(true)); };
  // Eine Karte EINMALIG nach vorne holen (Moment, in dem sie grün wird) und die
  // neue Reihenfolge für alle speichern. Danach bleibt sie frei verschiebbar.
  const loadAndPromote = async (fileId: number) => {
    let fresh: UploadedFileDto[];
    try { fresh = await listFiles(level); } catch { load(); return; }
    const order = [fileId, ...fresh.filter(f => f.id !== fileId).map(f => f.id)];
    const byId = new Map(fresh.map(f => [f.id, f] as const));
    setFiles(order.map(id => byId.get(id)).filter((f): f is UploadedFileDto => !!f));
    try { await reorderFiles(order); } catch { /* lokale Reihenfolge bleibt bestehen */ }
  };
  // refreshKey: erlaubt dem Parent (Editor), ein Neuladen nach einem externen
  // Save zu erzwingen (z.B. "Bericht PDF generieren" legt eine Datei am Vorgang an).
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [levelKey, refreshKey]);

  // Wartet ein verschickter Bericht auf den Rücklauf? Der Zustand hängt am Bericht
  // (Signaturrunde), nicht an der Datei — deshalb eine eigene Abfrage je Vorgang.
  const [berichtVersendetAm, setBerichtVersendetAm] = useState<string | null>(null);
  useEffect(() => {
    if (!('vorgangId' in level)) { setBerichtVersendetAm(null); return; }
    getBerichtVersand(level.vorgangId)
      .then(r => setBerichtVersendetAm(r.versendetAm))
      .catch(() => setBerichtVersendetAm(null));
    /* eslint-disable-next-line */
  }, [levelKey, refreshKey, files.length]);

  useEffect(() => {
    if (!lastDeleted) return;
    const t = setTimeout(() => setLastDeleted(null), 12000);
    return () => clearTimeout(t);
  }, [lastDeleted]);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pickCategory, setPickCategory] = useState<string>('');
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Vergleich auf Grundtyp: ohne "– Unterschrieben"-Suffix, case-insensitiv.
  // So zählt z.B. "Vollmacht" UND "Vollmacht – Unterschrieben" als Soll "Vollmacht".
  const baseCat = baseCategory;

  // Auch im Dauer-Grid (signedOnly) werden unsignierte Entwürfe der Soll-Typen ANGEZEIGT —
  // z.B. gerade generierte Vollmacht/GRC/De-Minimis, die noch auf die Unterschrift warten.
  // Ein Entwurf wird nur dann ausgeblendet, wenn es zu seinem Grundtyp eine signierte Fassung
  // gibt, die NEUER ist als er selbst — dann ist er durch die Unterschrift überholt (normaler
  // Ablauf: Entwurf generieren → Kunde unterschreibt → Entwurf verschwindet). Ist der Entwurf
  // dagegen NEUER als jede vorhandene Signatur (z.B. gerade per "Neu generieren" für die
  // jährliche Erneuerung erzeugt, während die alte — dann veraltete — Signatur noch dasteht),
  // bleibt er sichtbar. Ein reiner Kalenderjahres-Vergleich reicht hier NICHT: eine Erneuerung
  // im selben Jahr wie die alte Signatur (z.B. zum Testen, oder weil sich die Vollmacht
  // unterjährig ändert) muss genauso sichtbar bleiben.
  const sollBase = new Set(soll.map(s => baseCat(s)));
  const visibleFiles = files.filter(f => {
    if (isSigned(f.category)) return true;
    const cat = baseCat(f.category);
    const ownTime = new Date(f.createdAt).getTime();
    return !files.some(s => isSigned(s.category) && baseCat(s.category) === cat && new Date(s.createdAt).getTime() > ownTime);
  });
  const present = new Set(visibleFiles.map(f => baseCat(f.category)));
  const has = (s: string) => present.has(baseCat(s));
  const sollPresent = soll.filter(has).length;
  const missing = soll.filter(s => !has(s));
  const vollstaendig = soll.length > 0 && missing.length === 0;

  // Zusätzlich: welche Soll-Typen liegen UNTERSCHRIEBEN und aus DIESEM Kalenderjahr vor? Die
  // Dauer-Dokumente (Vollmacht/GRC/De-Minimis) gelten je Kalenderjahr — eine unterschriebene
  // Fassung aus dem Vorjahr zählt für das laufende Jahr nicht mehr. Getrennt von der
  // Sichtbarkeits-Regel oben (die entscheidet nur Entwurf vs. Signatur, nicht Compliance).
  const jahr = new Date().getFullYear();
  const signiertImJahr = new Set(
    files
      .filter(f => isSigned(f.category) && new Date(f.createdAt).getFullYear() === jahr)
      .map(f => baseCat(f.category)),
  );
  const fehlendAktuell = soll.filter(s => !signiertImJahr.has(baseCat(s)));

  // Stand nach oben melden (z.B. für den Haupt-Knopf der Aktionen-Spalte). Über einen
  // Schlüssel-String angebunden, damit eine inline übergebene Funktion keine Schleife auslöst.
  // Erst NACH dem Laden melden: vor der Antwort ist die Dateiliste leer, und ein
  // "es fehlt alles" würde beim Aufklappen kurz Reiter und Knöpfe aufblitzen lassen,
  // die gleich darauf wieder verschwinden.
  const statusKey = `${geladen}|${sollPresent}/${soll.length}|${missing.join(',')}|${fehlendAktuell.join(',')}`;
  useEffect(() => {
    if (!geladen) return;
    onStatus?.({ vorhanden: sollPresent, gesamt: soll.length, fehlend: missing, fehlendAktuell });
    /* eslint-disable-next-line */
  }, [statusKey]);

  // Zusammengeklappt: Kacheln liegen hinter einer Statuszeile. Was noch fehlt, wird nie
  // versteckt — dort steht die Arbeit, nicht im Archiv.
  const [kachelnOffen, setKachelnOffen] = useState<boolean | null>(null);
  const zusammenklappbar = !!collapse && (!geladen || collapse === 'immer' || vollstaendig);
  const kachelnSichtbar = !zusammenklappbar || (kachelnOffen ?? (geladen && missing.length > 0));
  // Liegt der Soll-Satz vollständig vor, ist der Einstieg „Andere Dokumente" nur noch
  // Beiwerk — bei den Dauer-Dokumenten wird er dann ausgeblendet.
  const uploadZeigen = !(uploadNurWennUnvollstaendig && vollstaendig);
  // Gemeinsame Mindesthöhe: eine leere „fehlt"-Kachel soll so hoch wirken wie eine
  // Dokumentkachel — und NICHT auf die Höhe einer Kachel mit Zusatzknopf wachsen.
  const kachelHoehe = 'min-h-[168px]';

  const presetCat = useRef<string | null>(null);
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    const preset = presetCat.current; presetCat.current = null;
    if (!file) return;
    setPendingFile(file);
    setPickCategory(preset ?? categories[0] ?? 'Sonstiges');
    setUploadErr(null);
  };
  const uploadFor = (cat: string) => { presetCat.current = categories.includes(cat) ? cat : (categories[0] ?? 'Sonstiges'); inputRef.current?.click(); };

  // Berater-Gegenzeichnung: lädt die unterschriebene Version DIREKT mit DERSELBEN
  // Kategorie der nur-Kunde-signierten Karte hoch (ohne Dokumenttyp-Dialog), damit der
  // Backend-Auto-Merge zündet (2 Unterschriften → „… – Vollständig unterschrieben").
  const countersignInputRef = useRef<HTMLInputElement>(null);
  const countersignSourceId = useRef<number | null>(null);
  const [countersigning, setCountersigning] = useState(false);
  const startCountersign = (sourceId: number) => { countersignSourceId.current = sourceId; countersignInputRef.current?.click(); };
  const onPickCountersign = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (countersignInputRef.current) countersignInputRef.current.value = '';
    const sourceId = countersignSourceId.current; countersignSourceId.current = null;
    if (!file || sourceId == null) return;
    setCountersigning(true); setUploadErr(null);
    try {
      const merged = await countersignFile(sourceId, file); // Backend mergt + ersetzt die Quell-Karte (versteckt)
      await loadAndPromote(merged.id); onChanged?.(); // gegengezeichnet → grün → einmalig nach vorne
    } catch (err: any) {
      setUploadErr(err?.message || 'Upload fehlgeschlagen');
    } finally { setCountersigning(false); }
  };
  // VN-Rücklauf: unterschriebene Seiten auf der orangen VN-Karte hochladen → synchron ins
  // Original-VN mergen (grün). Bei Nicht-Merge bleibt die Datei zur Prüfung liegen (rot).
  const vnUploadInputRef = useRef<HTMLInputElement>(null);
  const [vnUploading, setVnUploading] = useState(false);
  const startVnUpload = () => vnUploadInputRef.current?.click();
  const onPickVnUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (vnUploadInputRef.current) vnUploadInputRef.current.value = '';
    if (!file || !('vorgangId' in level)) return;
    setVnUploading(true); setUploadErr(null);
    try {
      const res = await uploadSignedVn(level.vorgangId, file);
      if (!res.merged) {
        setUploadErr(res.reason === 'keine-unterschrift'
          ? 'Keine Unterschrift erkannt – bitte die unterschriebenen Seiten hochladen.'
          : 'Konnte nicht automatisch zusammengeführt werden – die Datei liegt zur Prüfung bereit.');
      }
      await load(); onChanged?.();
    } catch (err: any) {
      setUploadErr(err?.message || 'Upload fehlgeschlagen');
    } finally { setVnUploading(false); }
  };
  // Rücklauf eines verschickten Berichts, der NICHT über den Kundenlink kam (Post, Mail,
  // eingescannt). Läuft als normaler Upload mit der Bericht-Kategorie: der Signatur-Scan
  // im Backend entscheidet dann wie beim Kunden-Upload, ob „Unterschrieben" oder
  // „Vollständig unterschrieben" daraus wird.
  const berichtUploadRef = useRef<HTMLInputElement>(null);
  const [berichtUploading, setBerichtUploading] = useState(false);
  const startBerichtUpload = () => berichtUploadRef.current?.click();
  const onPickBerichtUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (berichtUploadRef.current) berichtUploadRef.current.value = '';
    if (!file) return;
    setBerichtUploading(true); setUploadErr(null);
    try {
      const saved = await uploadFile(level, file, 'Bericht PDF', true);
      await loadAndPromote(saved.id); onChanged?.();
    } catch (err: any) {
      setUploadErr(err?.message || 'Upload fehlgeschlagen');
    } finally { setBerichtUploading(false); }
  };

  /** Diese Karte ist ein verschickter, noch nicht zurückgekommener Bericht. */
  const wartetAufRuecklauf = (cat: string | null) => !!berichtVersendetAm && isBerichtRoh(cat);

  /** Grün = fertig. Dieselbe Bedingung wie die Kachelfarbe: „zur Prüfung", „wartet auf
   *  Unterschrift" und der rohe VN sind trotz Unterschrift-Kennung NICHT fertig. */
  const istErledigt = (f: UploadedFileDto) =>
    !f.needsReview
    && !isPartiallySigned(f.category)
    && !isVnRaw(f.category)
    && !wartetAufRuecklauf(f.category)
    && (isComplete(f.category) || isVnComplete(f.category));

  // Erledigtes zuerst: so stehen die grünen Karten beieinander und was noch Arbeit
  // macht, folgt dahinter. Innerhalb der beiden Gruppen bleibt die selbst gewählte
  // Reihenfolge erhalten (stabile Sortierung).
  const angezeigteFiles = [...visibleFiles].sort((a, b) => Number(istErledigt(b)) - Number(istErledigt(a)));

  const [undoing, setUndoing] = useState<number | null>(null);
  // Manuelles Umkategorisieren (z.B. falsch erkanntes Dokument korrigieren). Setzt serverseitig
  // needsReview zurück ("rot" verschwindet), sobald der Berater die richtige Kategorie wählt.
  const [catEditFile, setCatEditFile] = useState<UploadedFileDto | null>(null);
  const [catEditValue, setCatEditValue] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const commitCategory = async () => {
    if (!catEditFile) return;
    const val = catEditValue.trim();
    if (!val) { setCatEditFile(null); return; }
    // Absichtlich AUCH speichern, wenn sich die Kategorie nicht ändert (Dialog ist auf die
    // aktuelle Kategorie vorausgefüllt) — sonst kann ein Berater eine bereits korrekt
    // erkannte, aber „zur Prüfung" markierte Datei nie bestätigen: „Speichern" muss auch
    // als reines "ja, das stimmt so" (→ needsReview löschen) funktionieren.
    setCatSaving(true);
    try { await setFileCategory(catEditFile.id, val); setCatEditFile(null); load(); onChanged?.(); }
    catch { /* Fehler: Modal offen lassen, Nutzer kann erneut versuchen */ }
    finally { setCatSaving(false); }
  };
  const handleUndoCountersign = async (greenId: number) => {
    setUndoing(greenId); setUploadErr(null);
    try { await undoCountersign(greenId); load(); onChanged?.(); }
    catch (err: any) { setUploadErr(err?.message || 'Rückgängig fehlgeschlagen'); }
    finally { setUndoing(null); }
  };

  // Manuelles Rettungsventil für einen "zur Prüfung" liegenden rohen VN: der Automatik-Merge
  // braucht ein ANDERES VN-Dokument am Vorgang als Ziel. Fehlt das (z.B. weil gescheiterte
  // Vorgänger-Uploads gelöscht wurden), bleibt die Karte sonst endlos gelb — "Kategorie ändern"
  // bietet die Zielkategorie bewusst nicht an (system-generiert, nicht frei wählbar). Dieser
  // Button setzt sie gezielt auf die grüne VN-Kategorie (Vorgang 67, 2026-10-21).
  const [promoting, setPromoting] = useState<number | null>(null);
  const promoteVn = async (f: UploadedFileDto) => {
    setPromoting(f.id); setUploadErr(null);
    try { await setFileCategory(f.id, 'Verwendungsnachweis – Vollständig unterschrieben'); load(); onChanged?.(); }
    catch (err: any) { setUploadErr(err?.message || 'Konnte nicht als vollständig markiert werden'); }
    finally { setPromoting(null); }
  };

  // "Neu generieren" für ein Dauer-Dokument (z.B. jährliche Erneuerung der Vollmacht).
  // Nur auf Kunden-Ebene sinnvoll — ersetzt einen unsignierten Entwurf, lässt eine
  // bereits signierte Fassung stehen (siehe BafaFormsService.generateOneForCustomer).
  const [regenerating, setRegenerating] = useState<number | null>(null);
  const regenerateDauer = async (f: UploadedFileDto) => {
    if (!('customerId' in level)) return;
    const typ = DAUER_TYP_SLUG[baseCategory(f.category)];
    if (!typ) return;
    setRegenerating(f.id); setUploadErr(null);
    try { await regenerateDauerdokument(level.customerId, typ); load(); onChanged?.(); }
    catch (err: any) { setUploadErr(err?.message || 'Neu generieren fehlgeschlagen'); }
    finally { setRegenerating(null); }
  };

  // "Signatur anfordern" für EIN Dauer-Dokument aus dem Menü heraus — vorausgewählt nur
  // dessen eigener Typ, weitere per Häkchen dazu wählbar (Dialog lädt die aktuelle Liste).
  const [sendFile, setSendFile] = useState<UploadedFileDto | null>(null);
  const [sendPreview, setSendPreview] = useState<{ preview: SigningPreview | null; loading: boolean }>({ preview: null, loading: false });
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const openSendDauer = (f: UploadedFileDto) => {
    if (!('customerId' in level)) return;
    setSendFile(f);
    setSendMsg(null);
    setSendPreview({ preview: null, loading: true });
    previewDauerSigning(level.customerId)
      .then(preview => setSendPreview({ preview, loading: false }))
      .catch(() => setSendPreview({ preview: null, loading: false }));
  };

  // Wird beim Upload eines Dauer-Typs eine Unterschrift erwartet? Dann läuft der
  // KI-Scan und der Button zeigt "Unterschrift wird geprüft…".
  const willScan = (cat: string) => /vollmacht|grc|minimis/i.test(cat) && !isSigned(cat);
  const [scanResult, setScanResult] = useState<{ fileId: number; name: string } | null>(null);

  const uploadAbort = useRef<AbortController | null>(null);
  const confirmUpload = async () => {
    if (!pendingFile) return;
    const ac = new AbortController();
    uploadAbort.current = ac;
    setUploading(true); setUploadErr(null);
    try {
      // "Sonstiges" (= Andere Dokumente) behält den Originalnamen; nur die
      // bekannten Dokumenttypen werden nach Muster umbenannt.
      const saved = await uploadFile(level, pendingFile, pickCategory, pickCategory !== 'Sonstiges', ac.signal);
      setPendingFile(null);
      // Beleg (Rechnung/Kontoauszug) wird mit dem Upload grün → einmalig nach vorne.
      if (isReceiptType(saved.category)) await loadAndPromote(saved.id);
      else load();
      onChanged?.(); // Eltern (z.B. Vorgangsliste) aktualisieren — etwa Restlaufzeit nach Bescheid-Upload.
      // signedOnly-Grid (Dauer): wurde keine Unterschrift erkannt, ist die Datei
      // ausgeblendet → Override anbieten, damit nichts unbemerkt verschwindet.
      if (signedOnly && sollBase.has(baseCat(pickCategory)) && !isSigned(saved.category)) {
        setScanResult({ fileId: saved.id, name: saved.originalName });
      }
    }
    catch (e: any) {
      if (e?.name === 'AbortError') { setPendingFile(null); load(); }
      else setUploadErr(e?.message || 'Upload fehlgeschlagen');
    }
    finally { setUploading(false); uploadAbort.current = null; }
  };

  const markSigned = async () => {
    if (!scanResult) return;
    setUploading(true);
    try { const id = scanResult.fileId; await setFileSigned(id, true); setScanResult(null); await loadAndPromote(id); }
    catch (e: any) { setUploadErr(e?.message || 'Konnte nicht markieren'); }
    finally { setUploading(false); }
  };

  const cancelUpload = () => {
    if (uploadAbort.current) uploadAbort.current.abort(); // laufenden Upload/Scan-Wait abbrechen
    setPendingFile(null); setUploadErr(null); setUploading(false);
  };

  // Mobile: pro Zeile ein Overflow-Menü (Aktionen gebündelt hinter 3-Punkte).
  // Fixe Position via Button-Rect → wird nicht von Overflow-Containern abgeschnitten.
  const [rowMenu, setRowMenu] = useState<{ id: number; top: number; right: number } | null>(null);
  const openRowMenu = (id: number, ev: React.MouseEvent<HTMLButtonElement>) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const r = ev.currentTarget.getBoundingClientRect();
    setRowMenu({ id, top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
  };

  const [preview, setPreview] = useState<{ url: string; type: string; name: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState<number | null>(null);
  const [pageEditorFile, setPageEditorFile] = useState<UploadedFileDto | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);

  // Inline-Umbenennen: Klick auf den Dateinamen macht ihn editierbar.
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const startRename = (f: UploadedFileDto) => { setEditId(f.id); setEditName(f.originalName); };
  const commitRename = async (f: UploadedFileDto) => {
    const v = editName.trim();
    setEditId(null);
    if (!v || v === f.originalName) return;
    setFiles(prev => prev.map(x => x.id === f.id ? { ...x, originalName: v } : x)); // optimistisch
    try { await renameFile(f.id, v); } catch { load(); } // bei Fehler: Server-Stand wiederherstellen
  };

  // Drag & Drop: Reihenfolge der Kacheln per Maus ändern; wird für alle gespeichert.
  // Gezogen wird NUR über den Griff (3 Punkte oben mittig) — die Kachel ist erst
  // draggable, wenn der Griff gedrückt wurde (kein versehentliches Ziehen, kein
  // 4-Richtungs-Cursor auf der ganzen Kachel).
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragHandleId, setDragHandleId] = useState<number | null>(null);
  const droppedRef = useRef(false);

  // Während des Ziehens die Liste LIVE umsortieren → die Kacheln machen Platz und
  // verschieben sich, sodass man in die entstehende Lücke ablegen kann.
  const handleDragOverTile = (targetId: number) => {
    if (dragId == null || dragId === targetId) return;
    setFiles(prev => {
      const arr = [...prev];
      const from = arr.findIndex(f => f.id === dragId);
      const to = arr.findIndex(f => f.id === targetId);
      if (from < 0 || to < 0 || from === to) return prev;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });
  };

  // Ablegen: die bereits live hergestellte Reihenfolge für alle speichern.
  const handleDrop = async () => {
    droppedRef.current = true;
    setDragId(null); setDragHandleId(null);
    const order = angezeigteFiles.map(f => f.id);
    try { await reorderFiles(order); onChanged?.(); }
    catch { load(); } // bei Fehler Server-Stand wiederherstellen
  };

  // Abbruch (außerhalb abgelegt / ESC): Server-Reihenfolge wiederherstellen.
  const handleDragEnd = () => {
    setDragId(null); setDragHandleId(null);
    if (!droppedRef.current) load();
    droppedRef.current = false;
  };

  // Authentifizierter Download: Datei MIT Bearer-Token als Blob laden, dann lokal
  // herunterladen. Ein einfacher <a href>-Link ginge ohne Token an den geschützten
  // Endpoint → 401 Unauthorized.
  const downloadFile = async (f: UploadedFileDto) => {
    setDownloading(f.id);
    try {
      const { url } = await fetchFileBlob(f.id);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.originalName || 'dokument.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Download fehlgeschlagen. Bitte neu anmelden und erneut versuchen.');
    } finally {
      setDownloading(null);
    }
  };

  const openPreview = async (f: UploadedFileDto) => {
    setPreviewLoading(f.id);
    try { const { url, type } = await fetchFileBlob(f.id); setPreview({ url, type, name: f.originalName }); }
    catch { await downloadFile(f); }
    finally { setPreviewLoading(null); }
  };
  const closePreview = () => { setPreview(p => { if (p) URL.revokeObjectURL(p.url); return null; }); };
  const isPdf = (p: { type: string; name: string }) => p.type.includes('pdf') || /\.pdf$/i.test(p.name);
  const isImage = (p: { type: string; name: string }) => p.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(p.name);

  return (
    <div>
      {lastDeleted && (
        <div className="flex items-center gap-3 px-3 py-2 mb-3 rounded-lg bg-slate-100 dark:bg-slate-700 text-sm text-slate-700 dark:text-slate-300">
          <Undo2 className="w-4 h-4 text-emerald-500 shrink-0" />
          <span className="flex-1">„{lastDeleted.name}" gelöscht</span>
          <button
            onClick={async () => {
                try {
                  await restoreFile(lastDeleted.id);
                  setLastDeleted(null);
                  load();
                } catch {
                  // Wiederherstellen fehlgeschlagen → Leiste bleibt, damit erneut versucht werden kann
                }
              }}
            className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium"
          >
            Rückgängig
          </button>
          <button onClick={() => setLastDeleted(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {/* Kopfzeile — je nach Klapp-Modus eine Statuszeile statt der Kachelwand. */}
      {collapse === 'immer' ? (
        <button
          type="button"
          onClick={() => setKachelnOffen(!kachelnSichtbar)}
          aria-expanded={kachelnSichtbar}
          title={vollstaendig ? 'Alle Dauer-Dokumente liegen vor' : `Fehlt: ${missing.map(dokumentLabel).join(', ')}`}
          className={`w-full flex items-center gap-2 px-2.5 py-2.5 rounded-lg border text-left transition-colors ${
            vollstaendig || !geladen
              ? 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40'
              : 'border-amber-300 bg-amber-50/60 hover:bg-amber-50 dark:border-amber-800 dark:bg-amber-900/10'
          } ${kachelnSichtbar ? 'mb-2' : ''}`}
        >
          {vollstaendig || !geladen
            ? <CheckCircle2 className={`w-4 h-4 shrink-0 ${geladen ? 'text-green-600' : 'text-slate-300 dark:text-slate-600'}`} />
            : <AlertCircle className="w-4 h-4 text-amber-500 shrink-0" />}
          <span className="text-sm font-bold text-slate-900 dark:text-white shrink-0">{title}</span>
          <span className="text-xs font-medium text-slate-500 shrink-0">{sollPresent} / {soll.length}</span>
          {/* Die Aufzählung der Typen stand hier nur als Wiederholung der Liste darunter —
              was fehlt, sagen Farbe, Zähler und Tooltip der Zeile. */}
          <span className="flex-1" />
          {/* Solange etwas fehlt: kurze Erklärung, worum es bei diesen Dokumenten geht.
              Liegen sie vollständig vor, ist die Frage beantwortet und das Zeichen weg. */}
          {geladen && !vollstaendig && (
            <HinweisPopover titel="Was sind Dauer-Dokumente?" text={DAUER_ERKLAERUNG} />
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${kachelnSichtbar ? '' : '-rotate-90'}`} />
        </button>
      ) : collapse === 'wenn-vollstaendig' && vollstaendig ? (
        <button
          type="button"
          onClick={() => setKachelnOffen(!kachelnSichtbar)}
          aria-expanded={kachelnSichtbar}
          className={`w-full flex items-center gap-2 px-2.5 py-2.5 rounded-lg border border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-900/10 text-left transition-colors hover:bg-green-100/70 dark:hover:bg-green-900/25 ${kachelnSichtbar ? 'mb-2' : ''}`}
        >
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          <span className="min-w-0 flex-1 text-xs font-medium text-green-800 dark:text-green-300 truncate">
            Alle Dokumente vollständig · {sollPresent}/{soll.length}
          </span>
          <span className="shrink-0 text-[13px] font-medium text-green-700 dark:text-green-400">
            {kachelnSichtbar ? 'Dokumente ausblenden' : 'Dokumente anzeigen'}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 text-green-600 shrink-0 transition-transform ${kachelnSichtbar ? '' : '-rotate-90'}`} />
        </button>
      ) : (
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h4>
          <span className="text-xs font-medium text-slate-500">
            {collapse === 'wenn-vollstaendig' && geladen && missing.length > 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                {missing.length === 1 ? '1 fehlt' : `${missing.length} fehlen`} ·{' '}
              </span>
            )}
            {sollPresent} / {soll.length}
          </span>
        </div>
      )}
      {/* Datei-Dialoge bleiben unabhängig von den Kacheln gemountet. */}
      <input ref={inputRef} type="file" className="hidden" onChange={onPick} />
      <input ref={countersignInputRef} type="file" className="hidden" onChange={onPickCountersign} />
      <input ref={vnUploadInputRef} type="file" accept="application/pdf" className="hidden" onChange={onPickVnUpload} />
      <input ref={berichtUploadRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={onPickBerichtUpload} />
      {kachelnSichtbar && (<>
      {/* Kompakte Zeilen: auf schmalen Schirmen immer, im Listen-Layout auch auf dem
          Desktop. Kein großes Datei-Icon, dafür ein farbiger Status-/Typ-Badge links.
          Kein Drag&Drop (auf Touch unpraktisch). */}
      <ul className={`space-y-1.5 ${layout === 'liste' ? '' : 'md:hidden'}`}>
        {angezeigteFiles.map(f => {
          const partial = isPartiallySigned(f.category);
          const complete = isComplete(f.category);
          const vnRaw = isVnRaw(f.category);
          const wartet = wartetAufRuecklauf(f.category);
          const typeLabel = fileTypeLabel(f);
          return (
            <li
              key={f.id}
              className={`flex items-center gap-2.5 p-2.5 rounded-lg border ${
                f.needsReview
                  ? 'border-red-300 bg-red-50/70 dark:border-red-900 dark:bg-red-900/15'
                  : (partial || vnRaw || wartet)
                  ? 'border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/10'
                  : complete || isVnComplete(f.category)
                  ? 'border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-900/10'
                  : 'border-slate-200 dark:border-slate-700'
              }`}
            >
              {/* Status-/Typ-Badge — needsReview (rot) hat Vorrang */}
              {f.needsReview ? (
                <span title={needsReviewTooltip(f.needsReviewReason)} className="shrink-0 w-7 h-7 rounded-full bg-red-500 text-white text-xs grid place-items-center">!</span>
              ) : (partial || vnRaw || wartet) ? (
                <span title={vnRaw ? 'Wartet auf die unterschriebenen Seiten — im Menü hochladen' : wartet ? 'Zur Unterschrift versendet — wartet auf den Rücklauf' : partialSignedTooltip(f.signatureState)} className="shrink-0 w-7 h-7 rounded-full bg-amber-500 text-white text-xs grid place-items-center">…</span>
              ) : (complete || isVnComplete(f.category)) ? (
                // Im Listen-Layout (Dauer-Dokumente) steht hier die Typ-Plakette statt des
                // Hakens: dass die Zeile erledigt ist, sagt schon ihr grüner Grund — was
                // fehlte, war der Hinweis, dass es sich um eine hochgeladene Datei handelt.
                layout === 'liste' ? (
                  // Erledigte Zeile: die Plakette bleibt in der Farbfamilie der Zeile,
                  // aber kräftig — Grau in Grün sah aus wie ausgegraut.
                  <span title={isVnComplete(f.category) ? 'Verwendungsnachweis vollständig unterschrieben' : isReceiptType(f.category) ? 'Hochgeladen' : 'Unterschrieben'} className="shrink-0 h-7 min-w-[28px] px-1.5 rounded-md bg-[#bfe6cf] text-green-800 dark:bg-green-900/40 dark:text-green-300 text-[11px] font-extrabold grid place-items-center">{typeLabel}</span>
                ) : (
                  <span title={isVnComplete(f.category) ? 'Verwendungsnachweis vollständig unterschrieben' : isReceiptType(f.category) ? 'Hochgeladen' : 'Unterschrieben'} className="shrink-0 w-7 h-7 rounded-full bg-green-600 text-white text-xs grid place-items-center">✓</span>
                )
              ) : (
                <span className="shrink-0 h-7 min-w-[28px] px-1.5 rounded-md bg-slate-200 text-slate-500 text-[11px] font-extrabold grid place-items-center">{typeLabel}</span>
              )}
              {/* Name + Kategorie */}
              <div className="min-w-0 flex-1">
                {editId === f.id ? (
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(f); } else if (e.key === 'Escape') { setEditId(null); } }}
                    onBlur={() => commitRename(f)}
                    className="w-full text-xs font-medium leading-tight rounded border border-blue-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-white px-1 py-0.5 outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => startRename(f)}
                    title={`${f.originalName} – klicken zum Umbenennen`}
                    className="block w-full text-left text-xs font-medium text-slate-800 dark:text-slate-200 leading-tight truncate hover:underline decoration-dotted cursor-text"
                  >{f.originalName}</button>
                )}
                <span className="flex items-center gap-1 text-[12px] text-slate-400 min-w-0">
                  <span className="truncate">{dokumentLabel(f.category)}</span>
                  {isVnComplete(f.category) && (
                    <span title="Verwendungsnachweis — Abschluss des Vorgangs" className="shrink-0 px-1 rounded border border-green-600 text-green-700 dark:text-green-400 dark:border-green-500 text-[10px] font-extrabold">VN</span>
                  )}
                </span>
              </div>
              {/* Aktionen — gebündelt im 3-Punkte-Menü */}
              <button
                onClick={(e) => openRowMenu(f.id, e)}
                aria-label="Aktionen"
                className={`shrink-0 p-1.5 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 ${
                  (previewLoading === f.id || downloading === f.id || undoing === f.id || regenerating === f.id) ? '' : ''
                }`}
              >
                {(previewLoading === f.id || downloading === f.id || undoing === f.id || regenerating === f.id || promoting === f.id || (countersigning && partial))
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <MoreVertical className="w-4 h-4" />}
              </button>
            </li>
          );
        })}
        {missing.map(cat => (
          <li key={cat}>
            <button onClick={() => uploadFor(cat)} title={`${dokumentLabel(cat)} hochladen`} className="w-full flex items-center gap-2.5 p-2.5 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700/40 transition-colors">
              <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white grid place-items-center"><Plus className="w-3.5 h-3.5" /></span>
              <span className="min-w-0 flex-1 text-left text-xs italic truncate">{dokumentLabel(cat)}</span>
              <span className="shrink-0 text-[12px]">fehlt</span>
            </button>
          </li>
        ))}
        {uploadZeigen && (
          <li>
            <button onClick={() => inputRef.current?.click()} className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 text-blue-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700/40 transition-colors">
              <span className="text-lg leading-none">+</span>
              <span className="text-xs font-semibold">Andere Dokumente</span>
            </button>
          </li>
        )}
      </ul>

      {/* Mobile-Zeilen-Menü (Portal, fixe Position) */}
      {rowMenu && (() => {
        const f = visibleFiles.find(x => x.id === rowMenu.id);
        if (!f) return null;
        const partial = isPartiallySigned(f.category);
        const run = (fn: () => void) => { setRowMenu(null); fn(); };
        return createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={() => setRowMenu(null)} />
            <div
              style={{ position: 'fixed', top: rowMenu.top, right: rowMenu.right }}
              className="w-52 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 py-1"
            >
              {partial && (
                <button onClick={() => run(() => startCountersign(f.id))} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-slate-600 transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Gegenzeichnen
                </button>
              )}
              {isVnRaw(f.category) && (
                <button onClick={() => run(() => startVnUpload())} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-slate-600 transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Unterschriebenen VN hochladen
                </button>
              )}
              {isVnRaw(f.category) && f.needsReview && (
                <button onClick={() => run(() => promoteVn(f))} title="Wenn die Datei bereits der vollständige, unterschriebene VN ist (automatischer Abgleich hatte kein Original zum Zusammenführen)" className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-slate-600 transition-colors">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Als vollständig markieren
                </button>
              )}
              {wartetAufRuecklauf(f.category) && (
                <button onClick={() => run(() => startBerichtUpload())} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-slate-600 transition-colors">
                  <Upload className="w-3.5 h-3.5" /> Unterschriebenen Bericht hochladen
                </button>
              )}
              {!partial && isSigned(f.category) && f.replacesFileId != null && (
                <button onClick={() => run(() => setPendingUndoCountersign(f))} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                  <Undo2 className="w-3.5 h-3.5" /> Gegenzeichnung rückgängig
                </button>
              )}
              <button onClick={() => run(() => openPreview(f))} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                <Eye className="w-3.5 h-3.5" /> Vorschau
              </button>
              <button onClick={() => run(() => downloadFile(f))} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                <Download className="w-3.5 h-3.5" /> Herunterladen
              </button>
              <button onClick={() => run(() => setPageEditorFile(f))} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                <FileStack className="w-3.5 h-3.5" /> Bearbeiten
              </button>
              {'customerId' in level && DAUER_TYP_SLUG[baseCategory(f.category)] && (
                <button onClick={() => run(() => regenerateDauer(f))} title="Erzeugt einen frischen, unsignierten Entwurf neu — eine bereits unterschriebene Fassung bleibt bis zur erneuten Unterschrift erhalten." className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5" /> Neu generieren
                </button>
              )}
              {'customerId' in level && DAUER_TYP_SLUG[baseCategory(f.category)] && !isSigned(f.category) && (
                <button onClick={() => run(() => openSendDauer(f))} title="Nur dieses Dokument zur Unterschrift senden — weitere können im Dialog dazugewählt werden." className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                  <Send className="w-3.5 h-3.5" /> Signatur anfordern
                </button>
              )}
              <button onClick={() => run(() => { setCatEditFile(f); setCatEditValue(f.category ?? ''); })} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors">
                <Tag className="w-3.5 h-3.5" /> Kategorie ändern
              </button>
              <button onClick={() => run(() => setPendingDelete(f))} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-600 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Löschen
              </button>
            </div>
          </>,
          document.body
        );
      })()}

      {layout !== 'liste' && (
      <div
        className="hidden md:grid items-start gap-2"
        style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(132px,1fr))' }}
        onDragOver={(e) => { if (dragId != null) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; } }}
      >
        {angezeigteFiles.map(f => (
          <div key={f.id} className="flex flex-col gap-1.5">
          <div
            draggable={dragHandleId === f.id}
            onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; droppedRef.current = false; setDragId(f.id); }}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; handleDragOverTile(f.id); }}
            onDrop={(e) => { e.preventDefault(); handleDrop(); }}
            className={`relative flex flex-col items-center gap-1.5 p-3 pt-5 rounded-xl border transition-transform ${kachelHoehe} ${
            dragId === f.id ? 'opacity-40' : ''
          } ${
            f.needsReview
              ? 'border-red-300 bg-red-50/70 dark:border-red-900 dark:bg-red-900/15'
              : (isPartiallySigned(f.category) || isVnRaw(f.category) || wartetAufRuecklauf(f.category))
              ? 'border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/10'
              : isComplete(f.category) || isVnComplete(f.category)
              ? 'border-green-200 bg-green-50/60 dark:border-green-900 dark:bg-green-900/10'
              : 'border-slate-200 dark:border-slate-700'
          }`}>
            {/* Zieh-Griff: 3 Punkte oben mittig — nur hier wird gegriffen/gezogen (4-Richtungs-Cursor). */}
            <button
              type="button"
              onMouseDown={() => setDragHandleId(f.id)}
              onMouseUp={() => setDragHandleId(null)}
              title="Zum Verschieben ziehen"
              className="absolute top-0.5 left-1/2 -translate-x-1/2 px-2 py-0.5 text-slate-300 hover:text-slate-500 dark:hover:text-slate-300 cursor-move"
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
            {!f.needsReview && (isPartiallySigned(f.category)
              ? <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-amber-500 text-white text-[12px] grid place-items-center" title={partialSignedTooltip(f.signatureState)}>…</span>
              : isVnRaw(f.category)
              ? <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-amber-500 text-white text-[12px] grid place-items-center" title="Wartet auf die unterschriebenen Seiten — unten hochladen">…</span>
              : wartetAufRuecklauf(f.category)
              ? <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-amber-500 text-white text-[12px] grid place-items-center" title={`Zur Unterschrift versendet${berichtVersendetAm ? ` am ${new Date(berichtVersendetAm).toLocaleDateString('de-DE')}` : ''} — wartet auf den Rücklauf`}>…</span>
              : (isComplete(f.category) || isVnComplete(f.category)) && <span title={isVnComplete(f.category) ? 'Verwendungsnachweis vollständig unterschrieben' : isReceiptType(f.category) ? 'Hochgeladen' : 'Unterschrieben'} className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-green-600 text-white text-[12px] grid place-items-center">✓</span>)}
            <div className="absolute top-1.5 left-1.5 flex items-center gap-1">
              {isVnComplete(f.category) && (
                <span title="Verwendungsnachweis — Abschluss des Vorgangs" className="h-4 px-1 grid place-items-center rounded border border-green-600 text-green-700 dark:text-green-400 dark:border-green-500 text-[10px] font-extrabold leading-none">VN</span>
              )}
              {!isPartiallySigned(f.category) && isSigned(f.category) && f.replacesFileId != null && (
                <button onClick={() => setPendingUndoCountersign(f)} disabled={undoing === f.id} title="Gegenzeichnung rückgängig machen (Kunden-Version wiederherstellen)" className="w-4 h-4 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 grid place-items-center hover:bg-slate-300 dark:hover:bg-slate-500 disabled:opacity-50">{undoing === f.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Undo2 className="w-2.5 h-2.5" />}</button>
              )}
            </div>
            {f.needsReview && (
              <span title={needsReviewTooltip(f.needsReviewReason)} className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[12px] grid place-items-center">!</span>
            )}
            <DocIcon label={fileTypeLabel(f)} />
            {editId === f.id ? (
              <input
                autoFocus
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(f); } else if (e.key === 'Escape') { setEditId(null); } }}
                onBlur={() => commitRename(f)}
                className="w-full text-[13px] text-center font-medium leading-tight rounded border border-blue-400 bg-white dark:bg-slate-700 text-slate-800 dark:text-white px-1 py-0.5 outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => startRename(f)}
                title={`${f.originalName} – klicken zum Umbenennen`}
                className="w-full text-[13px] text-center font-medium text-slate-800 dark:text-slate-200 leading-tight hover:underline decoration-dotted cursor-text"
                style={clamp2}
              >{f.originalName}</button>
            )}
            <span className="w-full text-[11px] text-center text-slate-400 truncate">{dokumentLabel(f.category)}</span>
            {isPartiallySigned(f.category) ? (
              // Nur vom Kunden unterschrieben → Gegenzeichnungs-Pfad für den Berater (Icons + Tooltips).
              <div className="flex items-center gap-1.5">
                <button onClick={() => openPreview(f)} title="Vorschau" className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700">{previewLoading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}</button>
                <button onClick={() => downloadFile(f)} disabled={downloading === f.id} title="Herunterladen, als Berater unterschreiben" className="p-1 rounded text-amber-600 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50">{downloading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}</button>
                <button onClick={(e) => openRowMenu(f.id, e)} title="Weitere Aktionen" className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"><MoreVertical className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex gap-1.5 text-slate-400">
                <button onClick={() => openPreview(f)} title="Vorschau" className="p-1 rounded hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700">{previewLoading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}</button>
                <button onClick={() => downloadFile(f)} disabled={downloading === f.id} title="Herunterladen" className="p-1 rounded hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 disabled:opacity-50">{downloading === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}</button>
                <button onClick={(e) => openRowMenu(f.id, e)} title="Weitere Aktionen" className="p-1 rounded hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"><MoreVertical className="w-4 h-4" /></button>
              </div>
            )}
          </div>
          {(isPartiallySigned(f.category) || isVnRaw(f.category) || wartetAufRuecklauf(f.category)) && (
            <button
              onClick={() => isVnRaw(f.category) ? startVnUpload() : wartetAufRuecklauf(f.category) ? startBerichtUpload() : startCountersign(f.id)}
              disabled={isVnRaw(f.category) ? vnUploading : wartetAufRuecklauf(f.category) ? berichtUploading : countersigning}
              title={isVnRaw(f.category)
                ? 'Unterschriebenen VN hochladen — wird automatisch ins Original zusammengeführt (grün)'
                : wartetAufRuecklauf(f.category)
                ? 'Vom Kunden unterschriebenen Bericht hochladen — falls er ihn nicht über den Link zurückgeschickt hat'
                : 'Gegengezeichnete Version hochladen — wird automatisch zum vollständigen Bericht zusammengeführt'}
              className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors"
            >
              {(isVnRaw(f.category) ? vnUploading : wartetAufRuecklauf(f.category) ? berichtUploading : countersigning)
                ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                : <Upload className="w-3.5 h-3.5 shrink-0" />}
              Signiert hochladen
            </button>
          )}
          </div>
        ))}
        {missing.map(cat => (
          <button key={cat} onClick={() => uploadFor(cat)} title={`${dokumentLabel(cat)} hochladen`} className={`relative flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border-2 border-dashed ${kachelHoehe}  border-slate-200 dark:border-slate-700 text-slate-400 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-700/40 transition-colors`}>
            <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-blue-600 text-white grid place-items-center"><Plus className="w-3 h-3" /></span>
            <DocIcon missing />
            <span className="w-full text-[13px] text-center italic leading-tight" style={clamp2}>{dokumentLabel(cat)}</span>
            <span className="text-[11px]">fehlt</span>
          </button>
        ))}
        {uploadZeigen && (
          <button onClick={() => inputRef.current?.click()} className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-blue-600 hover:border-blue-400 hover:bg-blue-50 ${kachelHoehe}`}>
            <span className="w-9 h-9 rounded-full bg-blue-50 grid place-items-center text-2xl leading-none">+</span>
            <span className="text-[13px] font-semibold text-center leading-tight">Andere Dokumente</span>
          </button>
        )}
      </div>
      )}
      </>)}

      {pendingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={cancelUpload}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Dokumenttyp wählen</h3>
            <p className="text-xs text-slate-500 mb-3 truncate" title={pendingFile.name}>{pendingFile.name}</p>
            <select
              value={pickCategory}
              onChange={e => setPickCategory(e.target.value)}
              className="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              {categories.map(c => <option key={c} value={c}>{dokumentLabel(c)}</option>)}
            </select>
            {willScan(pickCategory) && <p className="text-[13px] text-slate-400 mb-3 inline-flex items-center gap-1">🔍 Wird automatisch auf eine Unterschrift geprüft.</p>}
            {uploadErr && <p className="text-xs text-red-600 mb-3">{uploadErr}</p>}
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelUpload}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              >Abbrechen</button>
              <button
                onClick={confirmUpload}
                disabled={uploading}
                className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >{uploading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{uploading ? (willScan(pickCategory) ? 'Unterschrift wird geprüft…' : 'Lädt…') : 'Hochladen'}</button>
            </div>
          </div>
        </div>
      )}

      {catEditFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !catSaving && setCatEditFile(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Kategorie ändern</h3>
            <p className="text-xs text-slate-500 mb-3 truncate" title={catEditFile.originalName}>{catEditFile.originalName}</p>
            <select
              value={catEditValue}
              onChange={e => setCatEditValue(e.target.value)}
              className="w-full mb-4 px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              {(categories.includes(catEditValue) ? categories : [catEditValue, ...categories]).map(c => <option key={c} value={c}>{dokumentLabel(c)}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setCatEditFile(null)} disabled={catSaving} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">Abbrechen</button>
              <button onClick={commitCategory} disabled={catSaving} className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5">{catSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{catSaving ? 'Speichert…' : 'Speichern'}</button>
            </div>
          </div>
        </div>
      )}

      {sendFile && 'customerId' in level && (
        <SigningEmailReview
          open={!!sendFile}
          dauerCustomerId={level.customerId}
          email={customerEmail ?? ''}
          context={{}}
          docAuswahl={sendPreview}
          // Vorauswahl und Paket NICHT aus der nachgeladenen Vorschau ableiten: beim Öffnen ist
          // sie noch leer → nichts abgewählt, types=[] → Backend nimmt alle drei (WATTBLITZ).
          initialExcludedTypes={ALLE_DAUER_TYPEN.filter(t => t !== DAUER_TYP_SLUG[baseCategory(sendFile.category)])}
          prepare={(email, auswahl) => {
            const types = ALLE_DAUER_TYPEN.filter(t => !auswahl.excludeTypes.includes(t));
            return prepareSigningDauer(level.customerId, email, types);
          }}
          onCancel={() => setSendFile(null)}
          onSent={() => {
            setSendFile(null);
            setSendMsg('Zur Unterschrift gesendet.');
            load();
            onChanged?.();
          }}
        />
      )}
      {sendMsg && <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">{sendMsg}</p>}

      {scanResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setScanResult(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Keine Unterschrift erkannt</h3>
            <p className="text-xs text-slate-500 mb-4">Bei „{scanResult.name}" wurde keine handschriftliche Unterschrift gefunden. Das Dokument wurde als <strong>unsigniert</strong> gespeichert und ist in den Dauer-Dokumenten ausgeblendet. Falls es doch unterschrieben ist, kannst du es manuell markieren.</p>
            {uploadErr && <p className="text-xs text-red-600 mb-3">{uploadErr}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setScanResult(null)} disabled={uploading} className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">Belassen</button>
              <button onClick={markSigned} disabled={uploading} className="px-3 py-1.5 text-sm rounded-lg bg-green-600 text-white font-semibold hover:bg-green-700 disabled:opacity-50">Doch unterschrieben</button>
            </div>
          </div>
        </div>
      )}

      {pendingUndoCountersign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-2">Gegenzeichnung rückgängig machen?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
              „{pendingUndoCountersign.originalName}" wird durch die reine Kunden-Version ersetzt — die Gegenzeichnung geht verloren.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPendingUndoCountersign(null)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Abbrechen
              </button>
              <button
                disabled={undoing === pendingUndoCountersign.id}
                onClick={async () => {
                  const id = pendingUndoCountersign.id;
                  setPendingUndoCountersign(null);
                  await handleUndoCountersign(id);
                }}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {undoing === pendingUndoCountersign.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Rückgängig machen
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-2">Dokument löschen?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
              „{pendingDelete.originalName}" wird gelöscht.
            </p>
            {(isVnRaw(pendingDelete.category) || isVnComplete(pendingDelete.category)) &&
              files.filter(x => x.id !== pendingDelete.id && (isVnRaw(x.category) || isVnComplete(x.category))).length === 0 && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mb-3 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                ⚠ Das ist der einzige Verwendungsnachweis dieses Vorgangs. Ein später hochgeladener,
                unterschriebener Rücklauf kann danach nicht mehr automatisch zugeordnet werden.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Abbrechen
              </button>
              <button
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await deleteFile(pendingDelete.id);
                    setLastDeleted({ id: pendingDelete.id, name: pendingDelete.originalName });
                    setPendingDelete(null);
                    load();
                  } finally {
                    setDeleting(false);
                  }
                }}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
      {pageEditorFile && (
        <PageEditor file={pageEditorFile} otherFiles={files} onClose={() => { setPageEditorFile(null); load(); }} />
      )}
      {preview && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/70 p-4" onClick={closePreview}>
          <div className="flex items-center justify-between gap-3 mb-2 text-white shrink-0" onClick={e => e.stopPropagation()}>
            <span className="text-sm font-medium truncate" title={preview.name}>{preview.name}</span>
            <div className="flex items-center gap-2 shrink-0">
              <a href={preview.url} download={preview.name} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-white/15 hover:bg-white/25"><Download className="w-4 h-4" />Herunterladen</a>
              <button onClick={closePreview} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-white/15 hover:bg-white/25"><X className="w-4 h-4" />Schließen</button>
            </div>
          </div>
          <div className="flex-1 min-h-0 w-full" onClick={e => e.stopPropagation()}>
            {isPdf(preview) ? (
              <PdfScrollViewer url={preview.url} />
            ) : isImage(preview) ? (
              <div className="w-full h-full grid place-items-center">
                <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain rounded-lg" />
              </div>
            ) : (
              <div className="w-full h-full grid place-items-center text-center text-white/90">
                <div>
                  <p className="text-sm">Für diesen Dateityp ist keine Vorschau möglich.</p>
                  <a href={preview.url} download={preview.name} className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 text-sm rounded-lg bg-white/15 hover:bg-white/25"><Download className="w-4 h-4" />Herunterladen</a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Die Plakette nennt nur den Dateityp — sie ist Beschriftung, kein Status. Deshalb
 *  dezent grau: Rot und Blau lasen sich wie eine Warnung bzw. ein Hinweis und zogen
 *  die Aufmerksamkeit von den echten Statusfarben der Kachel ab. */
function DocIcon({ missing, label }: { missing?: boolean; label?: string }) {
  return (
    <div className={`relative w-[42px] h-[53px] rounded-md ${missing ? 'border-2 border-dashed border-slate-300 bg-slate-50' : 'border border-slate-300 bg-white shadow-sm'}`}>
      <span className={`absolute bottom-1.5 left-1.5 right-1.5 text-center text-[10px] font-extrabold rounded ${missing ? 'bg-slate-300 text-white' : 'bg-slate-200 text-slate-500'}`}>{missing ? '+' : (label || 'PDF')}</span>
    </div>
  );
}
